import { Dialog, DialogContent, Grid, IconButton, styled, Typography } from "@mui/material"
import { Box } from "@mui/system"
import React, { useEffect, useState } from "react"
import styles from "./EditSuggestedQuestion.module.scss"
import CloseIcon from "@mui/icons-material/Close"
import AppTextField from "components/StyledComponents/AppTextField"
import ImageIcon from "assets/icons/ImageIcon.svg"
import DraggableOptions from "../../../../../../components/DraggableOptions"
import { Formik } from "formik"
import { validationSchemaEditWeeklyQuestion } from "helpers/validationSchema"
import AppButton from "components/StyledComponents/AppButton"
import ImageUploadPopup from "../ImageUploadPopup"
import DeleteImageIcon from "assets/icons/closeIcon.svg"
import { useDispatch, useSelector } from "react-redux"
import { getTriviaCategoriesAction, uploadSuggestedQuestionImageAction } from "redux/L&D/actions"
import { learningDevelopmentActions } from "redux/L&D/slice/user"
import { editChallengeQuestionAction, weeklyCategoryAction } from "redux/marketing/actions"
import AppSelect from "components/StyledComponents/AppSelect"
import { Col } from "react-bootstrap"
import DraggableImageOptions from "components/DraggableImageOptions"
import DraggableMatchingOptions from "components/DraggableMatchingOptions"

const StyledDialog = styled(Dialog)(() => ({
  "& .MuiPaper-root": {
    backgroundColor: "transparent"
  }
}))

const questionTypeChoices = [
  { label: "Text", value: "text" },
  { label: "Matching", value: "matching" },
  { label: "2-Images", value: "2-images" },
  { label: "4-Images", value: "4-images" }
]

const EditSuggestedQuestion = ({ onClose = () => {}, data = {}, onQuestionEdited = () => {} }) => {
  const [question, setQuestion] = useState({
    optionSet: [{ name: "", isCorrect: false }]
  })
  const [questionInitOption, setQuestionInitOption] = useState([{ name: "", isCorrect: false }])
  const [originalOptionsBackup, setOriginalOptionsBackup] = useState(null)
  const [previousQuestionType, setPreviousQuestionType] = useState(null)
  const [isImagePopupOpen, setIsImagePopupOpen] = useState(false)
  const [selectedImage, setSelectedImage] = useState(null)
  const { image, isEdited } = useSelector((store) => store?.learningDevelopment?.suggestedQuestion)
  const { flavourImages } = useSelector((store) => store?.marketing?.wineOfTheMonth || {})
  const dispatch = useDispatch()

  const onImagePopupOpen = () => {
    setIsImagePopupOpen(true)
  }

  const onImagePopupClose = () => {
    setIsImagePopupOpen(false)
  }

  const onImageUpload = (newImage) => {
    let data = new FormData()
    data.append("media", newImage)
    data.append("media_type", "IMAGE")
    data.append("content_type", newImage.type)
    dispatch(uploadSuggestedQuestionImageAction(data))
  }

  const onImageDelete = () => {
    setSelectedImage(null)
    dispatch(learningDevelopmentActions.setSuggestedQuestionImage(null))
  }

  const onQuestionTypeSelect = (e, formik) => {
    const newQuestionType = e.value
    const currentQuestionType = formik.values?.questionType || previousQuestionType
    const currentOptions = formik.values?.optionSet || []

    // Store original options before switching to 2-images
    if (newQuestionType === "2-images" && currentQuestionType !== "2-images") {
      // Backup original options if we have more than 2 and they haven't been backed up yet
      if (currentOptions.length > 2 && !originalOptionsBackup) {
        setOriginalOptionsBackup([...currentOptions])
      }
    }

    // Restore original options when switching back from 2-images to other types
    if (
      currentQuestionType === "2-images" &&
      newQuestionType !== "2-images" &&
      originalOptionsBackup &&
      originalOptionsBackup.length > 2
    ) {
      const restoredOptions = originalOptionsBackup?.map((option) => {
        if (newQuestionType === "matching") {
          return {
            ...option,
            answer: option.answer || "",
            name: option.name || ""
          }
        }
        return {
          ...option,
          name: option.name || "",
          isCorrect: option.isCorrect !== undefined ? option.isCorrect : false
        }
      })
      formik.setFieldValue("optionSet", restoredOptions)
      setOriginalOptionsBackup(null)
    } else {
      if (newQuestionType === "matching") {
        const updatedOptions =
          currentOptions?.length > 0
            ? currentOptions?.map((option) => ({
                ...option,
                answer: option.answer || "",
                name: option.name || ""
              }))
            : [{ name: "", answer: "" }]
        formik.setFieldValue("optionSet", updatedOptions)
      } else if (currentOptions.length > 0) {
        const updatedOptions = currentOptions.map((option) => ({
          ...option,
          name: option.name || "",
          isCorrect: option.isCorrect !== undefined ? option.isCorrect : false
        }))
        formik.setFieldValue("optionSet", updatedOptions)
      }
    }

    setPreviousQuestionType(currentQuestionType)
    formik.setFieldValue("questionType", newQuestionType)
  }

  const initFormikData = () => {
    const isMatchingType = data?.question_type === "matching"
    const optionSet = isMatchingType
      ? data?.match_pairs?.map((option, index) => ({
          id: option.id,
          name: option.left_text,
          answer: option.right_text,
          order: option.order ?? index + 1
        })) || [{ name: "", answer: "", order: 1 }]
      : data?.answer?.map((option, index) => ({
          id: option.id,
          name: option.answer,
          image: option.answer_image
            ? { id: option.answer_image, url: option.answer_image_url }
            : "",
          isCorrect: option.is_correct,
          order: option.order ?? index + 1
        })) || [{ name: "", isCorrect: false, order: 1 }]
    const correctCount = optionSet?.filter((opt) => opt.isCorrect).length || 0
    const selection_mode = correctCount > 1 ? "MULTIPLE" : "SINGLE"

    setOriginalOptionsBackup(null)
    setPreviousQuestionType(data?.question_type || null)

    setQuestion({
      questionId: data.id,
      question: data?.title ?? "",
      questionType: data?.question_type,
      category: data?.category_id ?? "",
      filterCategory: data?.filter_category ?? "",
      difficultyLevel: data.level,
      duration: data.duration,
      optionSet,
      options_count: data?.optionSet?.length,
      selection_mode
    })
    dispatch(
      learningDevelopmentActions.setSuggestedQuestionImage({
        id: data.image_id,
        url: data.image
      })
    )
  }

  const initQuestionOptions = () => {
    setQuestionInitOption(
      data?.answer?.map((option, index) => ({
        id: option.id,
        name: option.answer,
        image: option.answer_image ? { id: option.answer_image, url: option.answer_image_url } : "",
        isCorrect: option.is_correct,
        order: option.order ?? index + 1
      }))
    )
  }

  const handleSubmit = (values) => {
    const isMatchingType = values?.questionType === "matching"
    const correctCount = isMatchingType
      ? values.optionSet?.length || 0
      : values.optionSet?.filter((opt) => opt.isCorrect).length || 0
    const selection_mode = correctCount > 1 ? "MULTIPLE" : "SINGLE"

    const payload = {
      image: image?.id || null,
      title: values.question,
      question_type: values?.questionType,
      category: data?.category_id,
      filter_category: data?.filter_category,
      related_food: data?.related_food,
      level: data.level,
      duration: data.duration,
      ...(isMatchingType
        ? {
            match_pairs: values?.optionSet?.map((option, index) => ({
              id: option.id,
              order: index + 1,
              left_text: option.name,
              right_text: option.answer
            }))
          }
        : {
            answers: values?.optionSet?.map((option, index) => ({
              id: option.id,
              answer: option.name,
              ...(option.answer_image || flavourImages[index]
                ? {
                    answer_image: option?.answer_image
                      ? option?.answer_image
                      : flavourImages[index]
                      ? flavourImages[index]
                      : null
                  }
                : {}),
              is_correct: option.isCorrect,
              order: index + 1
            }))
          }),
      options_count: values?.optionSet?.length,
      ...(!isMatchingType && { selection_mode })
    }
    dispatch(
      editChallengeQuestionAction({
        questionId: data.id,
        questionData: payload,
        weeklyChallenge: true,
        onClose: () => onClose()
      })
    )
  }

  useEffect(() => {
    initFormikData()
    initQuestionOptions()
  }, [data])

  useEffect(() => {
    dispatch(getTriviaCategoriesAction())
  }, [])

  useEffect(() => {
    if (isEdited) {
      onQuestionEdited()
      onClose()
    }
  }, [isEdited])

  useEffect(() => {
    dispatch(weeklyCategoryAction())
  }, [])

  return (
    <StyledDialog open={true} maxWidth="">
      <ImageUploadPopup
        onClose={onImagePopupClose}
        open={isImagePopupOpen}
        aspectHeight={1}
        aspectWidth={1}
        {...{
          selectedImage,
          setSelectedImage,
          onImageUpload,
          onImageDelete
        }}
      />
      <DialogContent id="editQuestionDialog" className={styles.dialogBox}>
        <Formik
          initialValues={question}
          validationSchema={validationSchemaEditWeeklyQuestion}
          context={{ questionType: question?.questionType }}
          enableReinitialize={true}
          onSubmit={handleSubmit}>
          {(formik) => {
            console.log("formik", formik.values, formik.errors, formik.touched)
            return (
              <React.Fragment>
                <Box className="d-flex justify-content-center position-relative">
                  <Typography variant="h2">{`Edit ${
                    data?.suggested_by ? "Suggested " : ""
                  }Question`}</Typography>
                  <Box className={styles.closeButton}>
                    <span role="button" onClick={onClose}>
                      <IconButton sx={(theme) => ({ color: theme.palette.text.primary })}>
                        <CloseIcon sx={{ height: "14px", width: "14px" }} />
                      </IconButton>
                    </span>
                  </Box>
                </Box>
                <Box height={40} />
                <Box sx={{ padding: "0 11px" }}>
                  <Box height={35} />
                  <Grid item mb={2}>
                    <Typography variant="h2" sx={{ fontSize: 20 }}>
                      Question Type
                    </Typography>
                    <Col item lg={4} className="mt-3">
                      <AppSelect
                        id={`formik.values.questionType`}
                        name={`formik.values.questionType`}
                        menuItems={questionTypeChoices}
                        isRequired={true}
                        value={formik.values?.questionType}
                        onMenuClick={(e) => onQuestionTypeSelect(e, formik)}
                        error={formik.touched.questionType && formik.errors.questionType}
                        touched={formik.touched.questionType}
                      />
                    </Col>
                  </Grid>
                  <Typography variant="h2" sx={{ fontSize: 20 }}>
                    Question
                  </Typography>
                  <Box height={19} />
                  <Box className="d-flex">
                    <AppTextField
                      id="question"
                      name="question"
                      placeholder="Enter Question"
                      value={formik.values.question}
                      onChange={formik.handleChange}
                      className="flex-grow-1 flex-shrink-1"
                      fullWidth
                      error={formik.touched.question && Boolean(formik.errors.question)}
                      helperText={formik.touched.question && formik.errors.question}
                    />
                    <img
                      role="button"
                      onClick={onImagePopupOpen}
                      src={ImageIcon}
                      className="flex-shrink-0 ms-4"
                      alt="Upload Image"
                    />
                  </Box>
                  {image?.url ? (
                    <Box className="mt-4 position-relative" sx={{ width: "fit-content" }}>
                      <img
                        style={{ height: 182, borderRadius: 6, width: "100%", maxWidth: "250px" }}
                        src={image.url}
                        alt=""
                      />
                      <img
                        role="button"
                        src={DeleteImageIcon}
                        alt=""
                        className="position-absolute"
                        style={{ top: 12, right: 12 }}
                        onClick={onImageDelete}
                      />
                    </Box>
                  ) : null}
                  {!!formik?.values?.questionType && formik?.values?.questionType === "matching" ? (
                    <DraggableMatchingOptions
                      formik={formik}
                      getOptionValues={(values = formik.values) => values?.optionSet}
                      getOptionPath={() => `optionSet`}
                    />
                  ) : !!formik?.values?.questionType &&
                    formik?.values?.questionType !== "text" &&
                    formik?.values?.questionType !== "soundbite" ? (
                    <DraggableImageOptions
                      formik={formik}
                      getOptionValues={(values = formik.values) => values?.optionSet}
                      getQuestionType={() => formik?.values?.questionType}
                      getOptionPath={() => `optionSet`}
                      questionInitOption={questionInitOption}
                    />
                  ) : (
                    <DraggableOptions
                      formik={formik}
                      getOptionValues={(values = formik.values) =>
                        values?.optionSet?.length !== 2 ? values?.optionSet : questionInitOption
                      }
                      getOptionPath={() => `optionSet`}
                    />
                  )}
                  <Box className="d-flex justify-content-center">
                    <AppButton
                      className="mt-3 px-5"
                      variant="outlined"
                      onClick={formik.handleSubmit}>
                      Save
                    </AppButton>
                  </Box>
                </Box>
              </React.Fragment>
            )
          }}
        </Formik>
      </DialogContent>
    </StyledDialog>
  )
}

export default EditSuggestedQuestion
